import { FEEDBACK_LIMITS } from '@social-studio/shared';

/**
 * Canvas side of the feedback annotator (spec 05 section 6.1): marks, their
 * drawing, hit testing and the export below the hub's size limit. Pure
 * functions over a 2D context; the component owns the state.
 */
export type Point = { readonly x: number; readonly y: number };
export type Tool = 'select' | 'arrow' | 'box' | 'pen' | 'text' | 'blur';
type Span<K extends 'arrow' | 'box' | 'blur'> = { readonly kind: K; readonly color: string; readonly from: Point; readonly to: Point };
export type Mark =
  | Span<'arrow'>
  | Span<'box'>
  | Span<'blur'>
  | { readonly kind: 'pen'; readonly color: string; readonly points: readonly Point[] }
  | { readonly kind: 'text'; readonly color: string; readonly at: Point; readonly text: string };

export type Measures = { readonly line: number; readonly font: number; readonly block: number };
type Rect = { x: number; y: number; w: number; h: number };

/** Mark colours (red, orange, blue, green); the names live in the locale file under the same keys. */
export const MARK_COLORS = [
  { key: 'red', value: '#E5484D' },
  { key: 'orange', value: '#F5A300' },
  { key: 'blue', value: '#2563EB' },
  { key: 'green', value: '#16A34A' },
] as const;
const HALO = 'rgba(255,255,255,.92)';
const DARK_INK = '#151515';
const LIGHT_INK = '#FFFFFF';
const isLight = (color: string) => color === MARK_COLORS[1].value;

/** Line width, font size and pixelation block follow the image size, so marks look alike on any capture. */
export function measuresOf(image: { width: number; height: number }): Measures {
  const width = Math.max(image.width, image.height * 0.6);
  return { line: Math.max(3, Math.round(width / 380)), font: Math.max(16, Math.round(width / 64)), block: Math.max(8, Math.round(width / 110)) };
}

const rectOf = (a: Point, b: Point): Rect => ({ x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), w: Math.abs(a.x - b.x), h: Math.abs(a.y - b.y) });

function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, radius: number) {
  const r = Math.min(radius, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function textBox(ctx: CanvasRenderingContext2D, mark: Extract<Mark, { kind: 'text' }>, m: Measures) {
  ctx.font = `600 ${m.font}px system-ui, sans-serif`;
  const pad = m.font * 0.45;
  const lines = mark.text.split('\n');
  const width = Math.max(...lines.map((line) => ctx.measureText(line).width));
  return { x: mark.at.x, y: mark.at.y, w: width + pad * 2, h: lines.length * m.font * 1.25 + pad * 1.4, pad, lines };
}

/** Draws one mark; a white halo keeps it visible on dark and busy backgrounds. */
export function drawMark(ctx: CanvasRenderingContext2D, mark: Mark, image: CanvasImageSource, m: Measures): void {
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  const haloWidth = m.line + Math.max(3, m.line * 0.9);
  const stroke = (path: () => void) => {
    ctx.strokeStyle = HALO;
    ctx.lineWidth = haloWidth;
    path();
    ctx.stroke();
    ctx.strokeStyle = mark.color;
    ctx.lineWidth = m.line;
    path();
    ctx.stroke();
  };
  if (mark.kind === 'box') {
    const r = rectOf(mark.from, mark.to);
    stroke(() => roundedRect(ctx, r.x, r.y, r.w, r.h, m.line * 2));
  } else if (mark.kind === 'pen') {
    stroke(() => {
      ctx.beginPath();
      mark.points.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
    });
  } else if (mark.kind === 'arrow') {
    drawArrow(ctx, mark, m, haloWidth);
  } else if (mark.kind === 'blur') {
    const r = rectOf(mark.from, mark.to);
    if (r.w >= 2 && r.h >= 2) {
      const small = document.createElement('canvas');
      small.width = Math.max(1, Math.round(r.w / m.block));
      small.height = Math.max(1, Math.round(r.h / m.block));
      small.getContext('2d')?.drawImage(image, r.x, r.y, r.w, r.h, 0, 0, small.width, small.height);
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(small, 0, 0, small.width, small.height, r.x, r.y, r.w, r.h);
    }
  } else {
    const box = textBox(ctx, mark, m);
    ctx.shadowColor = 'rgba(0,0,0,.25)';
    ctx.shadowBlur = m.font * 0.4;
    ctx.shadowOffsetY = m.font * 0.1;
    ctx.fillStyle = mark.color;
    roundedRect(ctx, box.x, box.y, box.w, box.h, box.pad);
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.fillStyle = isLight(mark.color) ? DARK_INK : LIGHT_INK;
    ctx.textBaseline = 'top';
    box.lines.forEach((line, i) => ctx.fillText(line, box.x + box.pad, box.y + box.pad * 0.7 + i * m.font * 1.25 + m.font * 0.05));
  }
  ctx.restore();
}

function drawArrow(ctx: CanvasRenderingContext2D, mark: Extract<Mark, { kind: 'arrow' }>, m: Measures, haloWidth: number) {
  const { from, to } = mark;
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  const length = Math.hypot(to.x - from.x, to.y - from.y) || 1;
  const head = Math.min(m.line * 5, length * 0.6);
  const base = { x: to.x - Math.cos(angle) * head * 0.8, y: to.y - Math.sin(angle) * head * 0.8 };
  const tip = () => {
    ctx.beginPath();
    ctx.moveTo(to.x, to.y);
    ctx.lineTo(to.x - head * Math.cos(angle - 0.5), to.y - head * Math.sin(angle - 0.5));
    ctx.lineTo(to.x - head * Math.cos(angle + 0.5), to.y - head * Math.sin(angle + 0.5));
    ctx.closePath();
  };
  const shaft = () => {
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(base.x, base.y);
  };
  ctx.strokeStyle = HALO;
  ctx.lineWidth = haloWidth;
  shaft();
  ctx.stroke();
  tip();
  ctx.stroke();
  ctx.strokeStyle = mark.color;
  ctx.fillStyle = mark.color;
  ctx.lineWidth = m.line;
  shaft();
  ctx.stroke();
  tip();
  ctx.fill();
  ctx.stroke();
}

export function boundsOf(ctx: CanvasRenderingContext2D, mark: Mark, m: Measures): Rect {
  if (mark.kind === 'text') return textBox(ctx, mark, m);
  if (mark.kind === 'pen') {
    const xs = mark.points.map((p) => p.x);
    const ys = mark.points.map((p) => p.y);
    return rectOf({ x: Math.min(...xs), y: Math.min(...ys) }, { x: Math.max(...xs), y: Math.max(...ys) });
  }
  return rectOf(mark.from, mark.to);
}

/** Dashed black-and-white frame around the selected mark. */
export function drawSelection(ctx: CanvasRenderingContext2D, mark: Mark, m: Measures): void {
  const r = boundsOf(ctx, mark, m);
  const pad = m.line * 2.5;
  ctx.save();
  ctx.lineWidth = Math.max(1.5, m.line / 2);
  ctx.setLineDash([m.line * 2, m.line * 1.5]);
  ctx.strokeStyle = DARK_INK;
  ctx.strokeRect(r.x - pad, r.y - pad, r.w + pad * 2, r.h + pad * 2);
  ctx.lineDashOffset = m.line * 1.75;
  ctx.strokeStyle = LIGHT_INK;
  ctx.strokeRect(r.x - pad, r.y - pad, r.w + pad * 2, r.h + pad * 2);
  ctx.restore();
}

function distanceToSegment(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSquared = dx * dx + dy * dy;
  const t = lengthSquared ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSquared)) : 0;
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/** Index of the topmost mark under `p`, or -1. */
export function hitTest(ctx: CanvasRenderingContext2D, marks: readonly Mark[], p: Point, tolerance: number, m: Measures): number {
  for (let i = marks.length - 1; i >= 0; i--) {
    const mark = marks[i]!;
    if (mark.kind === 'arrow') {
      if (distanceToSegment(p, mark.from, mark.to) <= tolerance + m.line) return i;
    } else if (mark.kind === 'pen') {
      if (mark.points.some((q, j) => j > 0 && distanceToSegment(p, mark.points[j - 1]!, q) <= tolerance + m.line)) return i;
    } else {
      const r = boundsOf(ctx, mark, m);
      if (p.x >= r.x - tolerance && p.x <= r.x + r.w + tolerance && p.y >= r.y - tolerance && p.y <= r.y + r.h + tolerance) return i;
    }
  }
  return -1;
}

export function moveMark(mark: Mark, dx: number, dy: number): Mark {
  const shift = (p: Point): Point => ({ x: p.x + dx, y: p.y + dy });
  if (mark.kind === 'pen') return { ...mark, points: mark.points.map(shift) };
  if (mark.kind === 'text') return { ...mark, at: shift(mark.at) };
  return { ...mark, from: shift(mark.from), to: shift(mark.to) };
}

/** A canvas holding `source`, scaled down to the longest edge the API accepts. */
export function toCanvas(source: CanvasImageSource, width: number, height: number): HTMLCanvasElement {
  const factor = Math.min(1, FEEDBACK_LIMITS.imageMaxEdge / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * factor);
  canvas.height = Math.round(height * factor);
  canvas.getContext('2d')?.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
}

/** WebP (JPEG where the browser cannot encode WebP) below the API limit: lower the quality first, then the size. */
export function toDataUrl(canvas: HTMLCanvasElement): string {
  const type = canvas.toDataURL('image/webp', 0.8).startsWith('data:image/webp') ? 'image/webp' : 'image/jpeg';
  let quality = 0.88;
  let source = canvas;
  for (let attempt = 0; attempt < 14; attempt++) {
    const data = source.toDataURL(type, quality);
    if (data.length <= FEEDBACK_LIMITS.imageMaxDataUrlLength) return data;
    if (quality > 0.62) quality -= 0.12;
    else source = toCanvas(source, source.width * 0.8, source.height * 0.8);
  }
  throw new Error('image-too-large');
}
